<template>
    <div class="container mx-auto">
        <!-- Introduction -->
        <ProductIntroSection
            subheading="Top-Aktien aus Hype-Sektoren"
            heading="Von D-Wave Quantum und Boston Scientific über Moderna und Workday bis zu Rigetti Computing. 15+ Top-Aktien aus Hype-Sektoren"
            :features="introFeatures"
            :product-group="productGroup"
            :selected-product-variant="selectedProductVariant"
            :sorted-product-variants="sortedProductVariants"
            pricing-title="Hype-Aktien-Paket"
            pricing-subtitle="Mit klarer Rotation und hoher Analysequalität"
            cta-text="Hype-Paket jetzt bestellen"
            cta-subtext="Sofort nach Buchung in Ihrem HKCM-Dashboard und in unserer App abrufbar."
            @select-product="$emit('select-product', $event)"
            @add-to-cart="$emit('add-to-cart')"
            @add-webinar-to-cart="
                () => {
                    $emit('add-webinar-to-cart');
                    $emit('add-to-cart');
                }
            "
        />

        <!-- So funktioniert's -->
        <HowItWorksSection
            title="So funktioniert unser Hype-Aktienpaket"
            :description="howItWorksDescription"
            features-title="Das erhalten Sie konkret:"
            :feature-cards="howItWorksCards"
        />

        <!-- Hype-Aktien Liste -->
        <ProductListSection title="Hype-Aktien-Liste" subtitle="17 Titel, davon 15 aktiv analysierte Aktien und 2 Titel auf Eis" :items="hypeAktienData.info" />

        <!-- Shopify -->
        <section
            v-if="productGroup.meta?.shopifyProductIds?.length > 0"
            class="lg:bg-gray-200 p-5 sm:p-10 lg:p-20 mb-20 lg:mb-0 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner"
        >
            <h2 class="text-center mb-4">{{ $t('products.hypeStocks.shopify.title') }}</h2>
            <div class="gap-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 place-self-center justify-items-stretch">
                <div v-for="shopifyProductId in productGroup.meta?.shopifyProductIds" :key="shopifyProductId" class="w-full h-full flex">
                    <ShopifyProduct :product-id="shopifyProductId" bg-color="bg-white" class="h-full" />
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20">
            <strong data-aos="fade-right">
                <h3 class="px-4 pt-10 text-center">{{ $t('products.hypeStocks.faq.heading') }}</h3>
            </strong>
            <div>
                <div v-for="(faq, key) in faqs" :key="key" data-aos="fade-up">
                    <Accordeon :title="faq.question" :content="faq.answer" />
                </div>
            </div>
        </section>

        <!-- Das bietet unsere Analyse -->
        <ProductAnalysisSection
            title="So arbeitet unsere Analyse"
            :description="analysisDescription"
            :features="analysisFeatures"
            :image-src="$asset('/hkcm/images/unsereAnalyse.png')"
            image-alt="HKCM-Analyse"
        />

        <!-- SEO Content -->
        <section>
            <div class="mx-auto px-0 sm:px-6 lg:px-8 py-14 prose prose-slate max-w-none">
                <!-- Was sind Hype-Aktien -->
                <div class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto justify-center">
                        <h2>{{ $t('products.hypeStocks.seo.whatAre.title') }}</h2>
                        <p class="text-slate-600" v-html="$t('products.hypeStocks.seo.whatAre.text')"></p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/hype_aktien_intro.jpg')" :alt="$t('products.hypeStocks.seo.whatAre.imageAlt')" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <!-- Warum Elliott-Wellen -->
                <div
                    class="bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 flex flex-col-reverse lg:flex-row-reverse items-center gap-8"
                >
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>{{ $t('products.hypeStocks.seo.elliott.title') }}</h2>
                        <p class="text-slate-600" v-html="$t('products.hypeStocks.seo.elliott.text')"></p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/hype_aktien_elliott.jpg')" :alt="$t('products.hypeStocks.seo.elliott.imageAlt')" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <!-- Pharma, AI, Space -->
                <div class="mb-20 lg:mb-0 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>{{ $t('products.hypeStocks.seo.sectors.title') }}</h2>
                        <p class="text-slate-600" v-html="$t('products.hypeStocks.seo.sectors.text')"></p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/hype_aktien_sectors.jpg')" :alt="$t('products.hypeStocks.seo.sectors.imageAlt')" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Floating CTA Button -->
        <FloatingCTAButton :is-visible="pricingCardInView" :text="$t('products.hypeStocks.floatingCta.text')" target-id="#pricing-card" />
    </div>
</template>

<script>
import hypeAktienData from '../../data/hype_aktien_testdaten.json';
import Accordeon from '../miscellaneous/Accordeon.vue';
import ShopifyProduct from '../miscellaneous/ShopifyProducts.vue';
import FloatingCTAButton from './FloatingCTAButton.vue';
import HowItWorksSection from './HowItWorksSection.vue';
import ProductAnalysisSection from './ProductAnalysisSection.vue';
import ProductIntroSection from './ProductIntroSection.vue';
import ProductListSection from './ProductListSection.vue';

export default {
    name: 'HypeAktien',
    components: {
        ProductIntroSection,
        HowItWorksSection,
        ProductListSection,
        ProductAnalysisSection,
        FloatingCTAButton,
        ShopifyProduct,
        Accordeon
    },
    props: {
        productGroup: {
            type: Object,
            required: true
        },
        selectedProductVariant: {
            type: Object,
            default: null
        },
        sortedProductVariants: {
            type: Array,
            default: () => []
        }
    },
    emits: ['select-product', 'add-to-cart', 'add-webinar-to-cart'],
    data: () => ({
        pricingCardInView: false,
        hypeAktienData,

        introFeatureDefs: [
            {
                titleKey: 'products.hypeStocks.intro.features.updatedDaily.title',
                descKey: 'products.hypeStocks.intro.features.updatedDaily.desc'
            },
            {
                titleKey: 'products.hypeStocks.intro.features.clearTrading.title',
                descKey: 'products.hypeStocks.intro.features.clearTrading.desc'
            },
            {
                titleKey: 'products.hypeStocks.intro.features.modelPortfolio.title',
                descKey: 'products.hypeStocks.intro.features.modelPortfolio.desc'
            },
            {
                titleKey: 'products.hypeStocks.intro.features.topStocks.title',
                descKey: 'products.hypeStocks.intro.features.topStocks.desc'
            }
        ],

        howItWorksCardDefs: [
            {
                titleKey: 'products.hypeStocks.howItWorks.cards.dailyWritten.title',
                textKey: 'products.hypeStocks.howItWorks.cards.dailyWritten.text',
                iconClass: 'fi fi-rr-envelope-open-text'
            },
            {
                titleKey: 'products.hypeStocks.howItWorks.cards.clearTrading.title',
                textKey: 'products.hypeStocks.howItWorks.cards.clearTrading.text',
                iconClass: 'fi fi-rr-chart-line-up'
            },
            {
                titleKey: 'products.hypeStocks.howItWorks.cards.tradersInvestors.title',
                textKey: 'products.hypeStocks.howItWorks.cards.tradersInvestors.text',
                iconClass: 'fi fi-rr-eye'
            },
            {
                titleKey: 'products.hypeStocks.howItWorks.cards.chartImages.title',
                textKey: 'products.hypeStocks.howItWorks.cards.chartImages.text',
                iconClass: 'fi fi-rr-bolt'
            },
            {
                titleKey: 'products.hypeStocks.howItWorks.cards.fastReactions.title',
                textKey: 'products.hypeStocks.howItWorks.cards.fastReactions.text',
                iconClass: 'fi fi-rr-medal'
            },
            {
                titleKey: 'products.hypeStocks.howItWorks.cards.weeklyAll.title',
                textKey: 'products.hypeStocks.howItWorks.cards.weeklyAll.text',
                iconClass: 'fi fi-rr-bullseye'
            }
        ],

        analysisFeatureDefs: [
            {
                titleKey: 'products.hypeStocks.analysis.features.structured.title',
                descKey: 'products.hypeStocks.analysis.features.structured.desc'
            },
            {
                titleKey: 'products.hypeStocks.analysis.features.targets.title',
                descKey: 'products.hypeStocks.analysis.features.targets.desc'
            },
            {
                titleKey: 'products.hypeStocks.analysis.features.transparency.title',
                descKey: 'products.hypeStocks.analysis.features.transparency.desc'
            }
        ],

        faqDefs: [
            {
                qKey: 'products.hypeStocks.faq.q.attractive.question',
                aKey: 'products.hypeStocks.faq.q.attractive.answer'
            },
            {
                qKey: 'products.hypeStocks.faq.q.extremeMoves.question',
                aKey: 'products.hypeStocks.faq.q.extremeMoves.answer'
            },
            {
                qKey: 'products.hypeStocks.faq.q.bestFuture.question',
                aKey: 'products.hypeStocks.faq.q.bestFuture.answer'
            },
            {
                qKey: 'products.hypeStocks.faq.q.whatInvest.question',
                aKey: 'products.hypeStocks.faq.q.whatInvest.answer'
            },
            {
                qKey: 'products.hypeStocks.faq.q.growthPotential.question',
                aKey: 'products.hypeStocks.faq.q.growthPotential.answer'
            },
            {
                qKey: 'products.hypeStocks.faq.q.risks.question',
                aKey: 'products.hypeStocks.faq.q.risks.answer'
            },
            {
                question: 'Was bedeutet „Analyse auf Eis“?',
                answer: 'Bei einzelnen Aktien kann die regelmäßige Analyse vorübergehend pausiert werden, ohne dass der Titel vollständig aus dem Hype-Aktienpaket entfernt wird. Diese Aktien bleiben sichtbar und werden entsprechend gekennzeichnet, sind jedoch nicht Bestandteil des regulären Analyse-Turnus.'
            }
        ],

        _pricingCardObserver: null
    }),

    computed: {
        introFeatures() {
            return this.introFeatureDefs.map((f) => ({
                title: this.$t(f.titleKey),
                desc: this.$t(f.descKey)
            }));
        },

        howItWorksDescription() {
            return 'Mit dem Hype-Aktienpaket der HKCM erhalten Sie ein aktiv rotiertes Setup. Das Paket umfasst 15 aktiv analysierte Hype-Aktien, die einer regelmäßigen Rotation unterliegen. Zusätzlich können einzelne Titel mit dem Status „Analyse auf Eis“ im Paket verbleiben. Diese sind vom regulären Analyse-Turnus ausgenommen, werden jedoch fortlaufend beobachtet. Sobald sich konkrete Handelsmöglichkeiten ergeben, erhalten Sie entsprechende Updates zu diesen Titeln.<br><br>Die Prognosen basieren auf der Elliott-Wellen-Analyse und liefern Ihnen präzise Zielzonen, die klar sichtbar machen, wann Einstiege, Ausstiege oder Anpassungen einer Position sinnvoll sein können. Ergänzend dazu pflegen wir ein transparentes Musterportfolio für Trader und Investoren, in dem alle Einstiege, Rotationen und Anpassungen nachvollziehbar dokumentiert werden, sodass Sie jederzeit eine klare Entscheidungsgrundlage haben.';
        },

        howItWorksCards() {
            return this.howItWorksCardDefs.map((c) => ({
                title: this.$t(c.titleKey),
                text: this.$t(c.textKey),
                iconClass: c.iconClass
            }));
        },

        analysisDescription() {
            return 'Unsere Analyse verbindet klare Szenarien, präzise Zielzonen und eine nachvollziehbare Dokumentation aller Anpassungen im Musterportfolio.';
        },

        analysisFeatures() {
            return this.analysisFeatureDefs.map((f) => ({
                title: this.$t(f.titleKey),
                desc: this.$t(f.descKey)
            }));
        },

        faqs() {
            return this.faqDefs.map((f) => ({
                question: f.question ?? this.$t(f.qKey),
                answer: f.answer ?? this.$t(f.aKey)
            }));
        }
    },

    mounted() {
        this.setupPricingObserver();
    },
    beforeUnmount() {
        if (this._pricingCardObserver) {
            this._pricingCardObserver.disconnect();
            this._pricingCardObserver = null;
        }
    },
    methods: {
        setupPricingObserver() {
            const el = document.getElementById('pricing-card');

            if (el) {
                this._pricingCardObserver = new IntersectionObserver(
                    (entries) => {
                        const entry = entries[0];
                        this.pricingCardInView = entry.isIntersecting;
                    },
                    { root: null, threshold: 0.1 }
                );
                this._pricingCardObserver.observe(el);
            } else {
                this.$nextTick(() => {
                    const el2 = document.getElementById('pricing-card');

                    if (el2) {
                        this._pricingCardObserver = new IntersectionObserver(
                            (entries) => {
                                this.pricingCardInView = entries[0].isIntersecting;
                            },
                            { root: null, threshold: 0.1 }
                        );
                        this._pricingCardObserver.observe(el2);
                    }
                });
            }
        }
    }
};
</script>
